import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useId, useRef } from 'react'

import type { MutableRefObject, ReactNode } from 'react'

type DialogProps = {
    open: boolean
    title: string
    onClose: () => void
    children: ReactNode
    footer?: ReactNode
    initialFocusRef?: MutableRefObject<HTMLElement | null>
    closeLabel: string
}

export const Dialog = ({ open, title, onClose, children, footer, initialFocusRef, closeLabel }: DialogProps) => {
    const titleId = useId()
    const panelRef = useRef<HTMLDivElement | null>(null)
    const closeButtonRef = useRef<HTMLButtonElement | null>(null)
    const previousFocusRef = useRef<HTMLElement | null>(null)
    const onCloseRef = useRef(onClose)

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (!open) {
            return
        }

        previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const timeout = window.setTimeout(() => {
            const focusTarget = initialFocusRef?.current ?? closeButtonRef.current ?? panelRef.current
            focusTarget?.focus()
        }, 0)

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onCloseRef.current()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKeyDown)
            window.clearTimeout(timeout)
            previousFocusRef.current?.focus()
        }
    }, [initialFocusRef, open])

    if (!open) {
        return null
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose()
                }
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className="bg-account-surface w-full max-w-md overflow-hidden rounded-2xl shadow-2xl outline-none"
            >
                <div className="border-account-divider flex items-center justify-between border-b px-6 py-4">
                    <h3 id={titleId} className="text-account-strong text-lg font-bold">
                        {title}
                    </h3>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        aria-label={closeLabel}
                        onClick={onClose}
                        className="text-account-faint hover:text-account-secondary p-1 transition"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-6 p-6">{children}</div>

                {footer && <div className="border-account-divider bg-account-muted border-t px-6 py-4">{footer}</div>}
            </div>
        </div>
    )
}
